import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Badge, Button, DateInput, NumberInput, Textarea, TextInput, Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui';
import { ChevronLeftIcon, ChevronRightIcon, DocumentTextIcon, DownloadIcon, LockIcon, PencilIcon, PlusIcon, SaveIcon, TrashIcon, UnlockIcon, UserPlusIcon, ViewColumnsIcon } from '../../components/ui/Icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../components/ui/Modal';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import {
  closeMgaPeriod,
  createMgaBondedLine,
  createMgaCashEntry,
  createMgaCrewRequest,
  deleteMgaBondedLine,
  deleteMgaCashEntry,
  deleteMgaCrewRequest,
  generateMgaBondedFromPrior,
  generateMgaPeriod,
  importMgaFromPrior,
  reopenMgaPeriod,
  updateMgaBondedLine,
  updateMgaCashEntry,
  updateMgaCrewRequest,
  updateMgaPeriod,
  updateMgaPeriodLines,
} from '../../services/mutations';
import {
  formatMgaStatus,
  moduleConfig,
  statusBadgeVariant,
} from './config';
import {
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaCrewRequests,
  useMgaPeriod,
  type MgaBondedLine,
  type MgaBondedSection,
  type MgaCashEntry,
  type MgaCashEntryType,
  type MgaColumnDef,
  type MgaCrewLine,
  type MgaCrewRequest,
  type MgaCrewRequestType,
  type MgaPeriod,
} from './useData';

type CellEditField =
  | 'daysInPeriod'
  | 'periodAmount'
  | 'extraEarnings'
  | 'previousBalance'
  | 'overtimeHours'
  | 'overtimeRate'
  | 'cashAdvanceOffice'
  | 'cashAdvanceMaster'
  | 'allotmentAmount'
  | 'cigarettesAmount'
  | 'internetCardsAmount'
  | 'otherBondedAmount'
  | 'variousDeductions'
  | 'requestedAllotment'
  | 'requestedCashByMaster'
  | 'remarks';

type CellEditTarget = {
  lineId: number;
  crewName: string;
  field: CellEditField;
  wageValueId?: number;
  columnCode: string;
  columnName: string;
  valueLabel: string;
  value: string;
  inputKind: 'number' | 'text';
};

type DraftLine = {
  id: number;
  daysInPeriod: string;
  extraEarnings: string;
  previousBalance: string;
  overtimeHours: string;
  overtimeRate: string;
  cashAdvanceOffice: string;
  cashAdvanceMaster: string;
  allotmentAmount: string;
  cigarettesAmount: string;
  internetCardsAmount: string;
  otherBondedAmount: string;
  variousDeductions: string;
  requestedAllotment: string;
  requestedCashByMaster: string;
  remarks: string;
  periodAmounts: Record<number, string>;
};

function formatAmt(n: number | null | undefined) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(n));
}

function dangerAmt(isDark: boolean) {
  return isDark ? 'text-red-400' : 'text-red-600';
}

function dangerDeleteBtn(isDark: boolean) {
  return isDark ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700';
}

function lineToDraft(line: MgaCrewLine): DraftLine {
  const periodAmounts: Record<number, string> = {};
  for (const v of line.wageValues ?? []) {
    periodAmounts[v.id] = v.periodAmount != null ? String(v.periodAmount) : '';
  }
  return {
    id: line.id,
    daysInPeriod: String(line.daysInPeriod ?? 0),
    extraEarnings: String(line.extraEarnings ?? 0),
    previousBalance: String(line.previousBalance ?? 0),
    overtimeHours: String(line.overtimeHours ?? 0),
    overtimeRate: String(line.overtimeRate ?? 0),
    cashAdvanceOffice: String(line.cashAdvanceOffice ?? 0),
    cashAdvanceMaster: String(line.cashAdvanceMaster ?? 0),
    allotmentAmount: String(line.allotmentAmount ?? 0),
    cigarettesAmount: String(line.cigarettesAmount ?? 0),
    internetCardsAmount: String(line.internetCardsAmount ?? 0),
    otherBondedAmount: String(line.otherBondedAmount ?? 0),
    variousDeductions: String(line.variousDeductions ?? line.deductions ?? 0),
    requestedAllotment: String(line.requestedAllotment ?? 0),
    requestedCashByMaster: String(line.requestedCashByMaster ?? 0),
    remarks: line.remarks ?? '',
    periodAmounts,
  };
}

function columnDefsForGrid(period: MgaPeriod | undefined): MgaColumnDef[] {
  const defs = period?.columnDefs ?? [];
  if (defs.length) {
    return defs
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
  }
  const map = new Map<string, MgaColumnDef>();
  for (const line of period?.lines ?? []) {
    for (const v of line.wageValues ?? []) {
      if (!map.has(v.columnCode)) {
        map.set(v.columnCode, {
          id: v.columnId ?? 0,
          code: v.columnCode,
          name: v.columnName,
          columnType: v.columnType,
          includeInTotal: v.includeInTotal,
          sortOrder: 0,
          isActive: true,
        });
      }
    }
  }
  return Array.from(map.values());
}

function errMsg(err: unknown, fallback: string) {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : fallback;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const PeriodGrid: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodId = id ? parseInt(id, 10) : NaN;
  const { item, isLoading, mutate } = useMgaPeriod(Number.isInteger(periodId) ? periodId : 0);
  const canEdit =
    can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_mga', 'edit');
  const canClose =
    can(moduleConfig.module.permissionModuleKey, 'close') || can('vessel_mga', 'close');
  const locked = item?.status === 'CLOSED';
  const editable = Boolean(canEdit && !locked && item?.status === 'OPEN');
  const closeApprovalStatus = item?.closeApproval?.status ?? null;
  const needsCloseResubmit = closeApprovalStatus === 'DRAFT';
  const closeApprovalPending = closeApprovalStatus === 'PENDING';
  const canSubmitClose = Boolean(canClose && item?.status === 'OPEN' && !closeApprovalPending);

  const tab = searchParams.get('tab') || 'payroll';
  const setTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const [drafts, setDrafts] = useState<Record<number, DraftLine>>({});
  const [dirty, setDirty] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [masterSignName, setMasterSignName] = useState('');
  const [cellEdit, setCellEdit] = useState<CellEditTarget | null>(null);
  const [cellEditValue, setCellEditValue] = useState('');
  /** When false, wage-scale columns collapse into a single Wage total. */
  const [showWageScale, setShowWageScale] = useState(() => {
    try {
      return sessionStorage.getItem('mga-show-wage-scale') === '1';
    } catch {
      return false;
    }
  });

  const setShowWageScalePersist = useCallback((next: boolean) => {
    setShowWageScale(next);
    try {
      sessionStorage.setItem('mga-show-wage-scale', next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!item?.lines) return;
    const next: Record<number, DraftLine> = {};
    for (const line of item.lines) next[line.id] = lineToDraft(line);
    setDrafts(next);
    setDirty(false);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    setNotesDraft(item.notes ?? '');
    setNotesDirty(false);
  }, [item?.id, item?.notes]);

  const wageColumns = useMemo(
    () => columnDefsForGrid(item).filter((c) => c.columnType !== 'INFO'),
    [item],
  );
  const lines = item?.lines ?? [];
  const totals = item?.totals;

  const updateDraft = useCallback((lineId: number, patch: Partial<DraftLine>) => {
    setDrafts((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], ...patch },
    }));
    setDirty(true);
  }, []);

  const updatePeriodAmount = useCallback((lineId: number, wageValueId: number, value: string) => {
    setDrafts((prev) => {
      const cur = prev[lineId];
      if (!cur) return prev;
      return {
        ...prev,
        [lineId]: {
          ...cur,
          periodAmounts: { ...cur.periodAmounts, [wageValueId]: value },
        },
      };
    });
    setDirty(true);
  }, []);

  const openCellEdit = useCallback(
    (target: CellEditTarget) => {
      if (!editable) return;
      setCellEdit(target);
      setCellEditValue(target.value);
    },
    [editable],
  );

  const closeCellEdit = useCallback(() => {
    setCellEdit(null);
    setCellEditValue('');
  }, []);

  const applyCellEdit = useCallback(() => {
    if (!cellEdit) return;
    if (cellEdit.field === 'periodAmount') {
      if (cellEdit.wageValueId != null) {
        updatePeriodAmount(cellEdit.lineId, cellEdit.wageValueId, cellEditValue);
      }
    } else {
      updateDraft(cellEdit.lineId, { [cellEdit.field]: cellEditValue });
    }
    closeCellEdit();
  }, [cellEdit, cellEditValue, updateDraft, updatePeriodAmount, closeCellEdit]);

  const editableCellClass = editable
    ? cn(colors.info, 'cursor-pointer select-none rounded px-1.5 py-0.5 -mx-0.5')
    : '';

  const tableColumns = useMemo((): Column<MgaCrewLine>[] => {
    const cols: Column<MgaCrewLine>[] = [
      {
        key: 'crewName',
        header: 'Crew',
        // Rank / nat / dates stacked under the name to free horizontal space for wage columns
        render: (_v, row) => {
          const meta = [row.rankName, row.nationalityName].filter(Boolean).join(' · ');
          const on = row.signOnDate ? formatDateGlobal(row.signOnDate) : '—';
          const off = row.signOffDate ? formatDateGlobal(row.signOffDate) : '—';
          return (
            <div className="flex min-w-[11rem] max-w-[16rem] items-start gap-1.5">
              <Link
                to={`/mga/${periodId}/lines/${row.id}`}
                className={cn(
                  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                  colors.primaryFg,
                  colors.interactiveHover,
                )}
                title="Monthly pay account"
                aria-label={`Open monthly pay account for ${row.crewName}`}
              >
                <DocumentTextIcon className="h-4 w-4" />
              </Link>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={cn('text-sm font-medium leading-snug', colors.text)}>
                    {row.crewName}
                  </span>
                  {row.missingWageSplit && (
                    <Badge variant="secondary" className="rounded-md px-1.5 py-0 text-[10px]">
                      No wage split
                    </Badge>
                  )}
                </div>
                {meta ? (
                  <p className={cn('mt-0.5 text-xs leading-snug', colors.textMuted)}>{meta}</p>
                ) : null}
                <p className={cn('mt-0.5 text-[11px] tabular-nums leading-snug', colors.textMuted)}>
                  {on} → {off}
                </p>
              </div>
            </div>
          );
        },
        footerType: 'custom',
        footerRender: () =>
          totals ? <span className="font-semibold">Totals ({totals.lineCount})</span> : null,
      },
    ];

    // Days + dynamic wages + settlement need per-row draft; build via render that reads drafts
    cols.push({
      key: 'daysInPeriod',
      header: 'Days',
      align: 'right',
      render: (_v, row) => {
        const draft = drafts[row.id] ?? lineToDraft(row);
        return (
          <span
            className={cn('block text-sm tabular-nums text-right', colors.text, editableCellClass)}
            title={editable ? 'Double-click to edit' : undefined}
            onDoubleClick={(e) => {
              e.stopPropagation();
              openCellEdit({
                lineId: row.id,
                crewName: row.crewName,
                field: 'daysInPeriod',
                columnCode: 'Days',
                columnName: 'Days in period',
                valueLabel: 'Days',
                value: draft.daysInPeriod,
                inputKind: 'number',
              });
            }}
          >
            {formatAmt(draft.daysInPeriod === '' ? null : Number(draft.daysInPeriod))}
          </span>
        );
      },
    });

    const buildWageCol = (col: (typeof wageColumns)[number]): Column<MgaCrewLine> => {
      const isAllotCol = col.code.toUpperCase() === 'ALLOT';
      return {
        key: `wage:${col.code}`,
        header: col.code,
        align: 'right',
        footerType: isAllotCol ? 'custom' : undefined,
        footerRender: isAllotCol
          ? () => (
              <span className={dangerAmt(isDark)}>
                {formatAmt(totals?.allotmentAmount)}
              </span>
            )
          : undefined,
        render: (_v, row) => {
          // ALLOT = sum of allotment requests for this crew (synced into allotmentAmount)
          if (isAllotCol) {
            const draft = drafts[row.id] ?? lineToDraft(row);
            const amt = Number(draft.allotmentAmount) || Number(row.allotmentAmount) || 0;
            return (
              <span
                className={cn('block text-sm tabular-nums text-right', dangerAmt(isDark))}
                title="Sum of allotment requests"
              >
                {formatAmt(amt)}
              </span>
            );
          }
          const draft = drafts[row.id] ?? lineToDraft(row);
          const wv = (row.wageValues ?? []).find((x) => x.columnCode === col.code);
          const displayValue = wv
            ? draft.periodAmounts[wv.id] ?? (wv.periodAmount != null ? String(wv.periodAmount) : '')
            : '';
          const canOpenEdit = Boolean(wv && editable);
          return (
            <span
              className={cn(
                'block text-sm tabular-nums text-right',
                col.columnType === 'DEDUCTION' ? dangerAmt(isDark) : colors.text,
                canOpenEdit && editableCellClass,
              )}
              title={canOpenEdit ? 'Double-click to edit' : undefined}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!wv || !editable) return;
                openCellEdit({
                  lineId: row.id,
                  crewName: row.crewName,
                  field: 'periodAmount',
                  wageValueId: wv.id,
                  columnCode: col.code,
                  columnName: col.name,
                  valueLabel: 'Period amount',
                  value: displayValue,
                  inputKind: 'number',
                });
              }}
            >
              {wv ? formatAmt(displayValue === '' ? null : Number(displayValue)) : '—'}
            </span>
          );
        },
      };
    };

    const afterExtraCodes = new Set(['ALLOT', 'OTHER_DED']);
    const wageScaleCols = wageColumns.filter(
      (c) => !afterExtraCodes.has(c.code.toUpperCase()),
    );

    const rowWageScaleTotal = (row: MgaCrewLine) => {
      const draft = drafts[row.id] ?? lineToDraft(row);
      let sum = 0;
      for (const col of wageScaleCols) {
        if (col.columnType === 'DEDUCTION') continue;
        const wv = (row.wageValues ?? []).find((x) => x.columnCode === col.code);
        if (!wv || !col.includeInTotal) continue;
        const raw =
          draft.periodAmounts[wv.id] ??
          (wv.periodAmount != null ? String(wv.periodAmount) : '');
        sum += Number(raw) || 0;
      }
      return sum;
    };

    if (showWageScale) {
      wageScaleCols.forEach((col, idx) => {
        const base = buildWageCol(col);
        if (idx === 0) {
          cols.push({
            ...base,
            header: (
              <span className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center rounded p-0.5 hover:opacity-80',
                    colors.primaryFg,
                  )}
                  title="Hide wage scale columns"
                  aria-label="Hide wage scale columns"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWageScalePersist(false);
                  }}
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                </button>
                <span>{col.code}</span>
              </span>
            ),
          });
        } else {
          cols.push(base);
        }
      });
    } else if (wageScaleCols.length) {
      cols.push({
        key: 'wageScaleTotal',
        header: (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 uppercase tracking-wider hover:underline',
              colors.primaryFg,
            )}
            title="Show individual wage columns"
            onClick={(e) => {
              e.stopPropagation();
              setShowWageScalePersist(true);
            }}
          >
            Wage total
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        ),
        align: 'right',
        footerType: 'custom',
        footerRender: (_v, all) =>
          formatAmt(all.reduce((s, row) => s + rowWageScaleTotal(row), 0)),
        render: (_v, row) => {
          const total = rowWageScaleTotal(row);
          const parts = wageScaleCols
            .filter((c) => c.columnType !== 'DEDUCTION' && c.includeInTotal)
            .map((c) => {
              const draft = drafts[row.id] ?? lineToDraft(row);
              const wv = (row.wageValues ?? []).find((x) => x.columnCode === c.code);
              if (!wv) return null;
              const raw =
                draft.periodAmounts[wv.id] ??
                (wv.periodAmount != null ? String(wv.periodAmount) : '');
              const n = Number(raw) || 0;
              if (!n) return null;
              return `${c.code} ${formatAmt(n)}`;
            })
            .filter(Boolean);
          return (
            <button
              type="button"
              className={cn(
                'block w-full text-sm font-medium tabular-nums text-right hover:underline',
                colors.text,
              )}
              title={
                parts.length
                  ? `${parts.join(' · ')} — click to show columns`
                  : 'Click to show wage columns'
              }
              onClick={(e) => {
                e.stopPropagation();
                setShowWageScalePersist(true);
              }}
            >
              {formatAmt(total)}
            </button>
          );
        },
      });
    }

    const settlement = (
      field: Exclude<CellEditField, 'periodAmount' | 'remarks'>,
      code: string,
      label: string,
      footer?: number | null,
      red?: boolean,
    ): Column<MgaCrewLine> => ({
      key: field,
      header: code,
      align: 'right',
      footerType: footer != null ? 'custom' : undefined,
      footerRender: footer != null ? () => formatAmt(footer) : undefined,
      render: (_v, row) => {
        const draft = drafts[row.id] ?? lineToDraft(row);
        const draftVal = String(draft[field] ?? '');
        return (
          <span
            className={cn(
              'block text-sm tabular-nums text-right',
              red ? dangerAmt(isDark) : colors.text,
              editableCellClass,
            )}
            title={editable ? 'Double-click to edit' : undefined}
            onDoubleClick={(e) => {
              e.stopPropagation();
              openCellEdit({
                lineId: row.id,
                crewName: row.crewName,
                field,
                columnCode: code,
                columnName: label,
                valueLabel: label,
                value: draftVal,
                inputKind: 'number',
              });
            }}
          >
            {formatAmt(draftVal === '' ? null : Number(draftVal))}
          </span>
        );
      },
    });

    cols.push(
      settlement('overtimeHours', 'OT Hrs', 'Overtime hours'),
      settlement('overtimeRate', 'OT Rate', 'Overtime rate'),
      {
        key: 'otTotal',
        header: 'OT Total',
        align: 'right',
        footerType: 'custom',
        footerRender: () => formatAmt(totals?.otTotal),
        render: (_v, row) => {
          const draft = drafts[row.id] ?? lineToDraft(row);
          const ot = (Number(draft.overtimeHours) || 0) * (Number(draft.overtimeRate) || 0);
          return (
            <span className={cn('text-sm tabular-nums whitespace-nowrap', colors.text)}>
              {formatAmt(ot)}
            </span>
          );
        },
      },
      settlement('previousBalance', 'Prev.', 'Previous balance', totals?.previousBalance),
      settlement('extraEarnings', 'Extra', 'Extra earnings', totals?.extraEarnings),
    );

    // ALLOT → OTHER_DED immediately after Extra
    for (const code of ['ALLOT', 'OTHER_DED'] as const) {
      const col = wageColumns.find((c) => c.code.toUpperCase() === code);
      if (col) cols.push(buildWageCol(col));
    }

    cols.push(
      settlement('cashAdvanceOffice', 'Office adv.', 'Cash advance by office', undefined, true),
      {
        key: 'cashAdvanceMaster',
        header: 'Master adv.',
        align: 'right',
        render: (_v, row) => {
          const draft = drafts[row.id] ?? lineToDraft(row);
          const amt = Number(draft.cashAdvanceMaster) || Number(row.cashAdvanceMaster) || 0;
          return (
            <span
              className={cn('block text-sm tabular-nums text-right', dangerAmt(isDark))}
              title="Sum of cash-by-master requests + cash-book expenses linked to this crew"
            >
              {formatAmt(amt)}
            </span>
          );
        },
      },
      // Allotments appear in ALLOT (sum of crew allotment requests) — no separate Allot. column
      settlement('cigarettesAmount', 'Cig.', 'Cigarettes', undefined, true),
      settlement('internetCardsAmount', 'Internet', 'Internet cards', undefined, true),
      settlement('otherBondedAmount', 'Bonded', 'Other bonded', undefined, true),
      settlement(
        'variousDeductions',
        'Various',
        'Various deductions',
        undefined,
        true,
      ),
      {
        key: 'totalDeductions',
        header: 'Tot. Ded.',
        align: 'right',
        footerType: 'custom',
        footerRender: () => (
          <span className={dangerAmt(isDark)}>
            {formatAmt(totals?.totalDeductions ?? totals?.deductions)}
          </span>
        ),
        render: (_v, row) => (
          <span className={cn('text-sm tabular-nums whitespace-nowrap', dangerAmt(isDark))}>
            {formatAmt(row.totalDeductions ?? row.deductions)}
          </span>
        ),
      },
      {
        key: 'netDue',
        header: 'Balance',
        align: 'right',
        footerType: 'custom',
        footerRender: () => (
          <span className="font-semibold">{formatAmt(totals?.netDue)}</span>
        ),
        render: (v) => (
          <span className={cn('text-sm font-semibold tabular-nums whitespace-nowrap', colors.text)}>
            {formatAmt(v)}
          </span>
        ),
      },
      {
        key: 'remarks',
        header: 'Remarks',
        maxWidth: '12rem',
        render: (_v, row) => {
          const draft = drafts[row.id] ?? lineToDraft(row);
          return (
            <span
              className={cn(
                'block max-w-[12rem] truncate text-sm',
                colors.textMuted,
                editableCellClass,
              )}
              title={editable ? draft.remarks || 'Double-click to edit' : draft.remarks || undefined}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!editable) return;
                openCellEdit({
                  lineId: row.id,
                  crewName: row.crewName,
                  field: 'remarks',
                  columnCode: 'Remarks',
                  columnName: 'Remarks',
                  valueLabel: 'Remarks',
                  value: draft.remarks,
                  inputKind: 'text',
                });
              }}
            >
              {draft.remarks || '—'}
            </span>
          );
        },
      },
    );

    return cols;
  }, [
    wageColumns,
    drafts,
    editable,
    editableCellClass,
    openCellEdit,
    colors,
    totals,
    periodId,
    showWageScale,
    setShowWageScalePersist,
    isDark,
  ]);

  const handleGenerate = async () => {
    if (!item || !canEdit) return;
    setBusy(true);
    try {
      const result = await generateMgaPeriod(item.id);
      const n = result.generatedCount ?? 0;
      showSuccess(n ? `Generated ${n} crew line${n === 1 ? '' : 's'}.` : 'No new crew lines to add.');
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Generate failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleImportFromPrior = async () => {
    if (!item || !canEdit || locked) return;
    setBusy(true);
    try {
      const res = await importMgaFromPrior(item.id);
      const parts = [
        res.previousBalancesUpdated
          ? `${res.previousBalancesUpdated} previous balance(s)`
          : null,
        res.bondedStockCopied ? `${res.bondedStockCopied} remaining stock line(s)` : null,
        res.openingCashCreated ? 'opening cash' : null,
      ].filter(Boolean);
      showSuccess(
        parts.length
          ? `Imported from prior CLOSED MGA: ${parts.join(', ')}.`
          : 'Prior CLOSED MGA found, but nothing new to import (balances already set / stock & cash already present).',
      );
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Import requires a previous CLOSED MGA for this vessel.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!item || !editable) return;

    if (tab === 'notes') {
      setBusy(true);
      try {
        await updateMgaPeriod(item.id, { notes: notesDraft.trim() || null });
        showSuccess('Notes saved.');
        setNotesDirty(false);
        await mutate();
      } catch (err: unknown) {
        showError(errMsg(err, 'Save failed.'));
      } finally {
        setBusy(false);
      }
      return;
    }

    const payload = Object.values(drafts).map((d) => ({
      id: d.id,
      daysInPeriod: Number(d.daysInPeriod) || 0,
      extraEarnings: Number(d.extraEarnings) || 0,
      previousBalance: Number(d.previousBalance) || 0,
      overtimeHours: Number(d.overtimeHours) || 0,
      overtimeRate: Number(d.overtimeRate) || 0,
      cashAdvanceOffice: Number(d.cashAdvanceOffice) || 0,
      cigarettesAmount: Number(d.cigarettesAmount) || 0,
      internetCardsAmount: Number(d.internetCardsAmount) || 0,
      otherBondedAmount: Number(d.otherBondedAmount) || 0,
      variousDeductions: Number(d.variousDeductions) || 0,
      remarks: d.remarks.trim() || null,
      wageValues: Object.entries(d.periodAmounts).map(([wid, amt]) => ({
        id: Number(wid),
        periodAmount: Number(amt) || 0,
      })),
    }));
    setBusy(true);
    try {
      await updateMgaPeriodLines(item.id, { lines: payload });
      showSuccess('Sheet saved.');
      setDirty(false);
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!item || !canSubmitClose) return;
    if (dirty || notesDirty) {
      showError('Save changes before closing the period.');
      return;
    }
    setCloseConfirmOpen(true);
  };

  const confirmClose = async () => {
    if (!item || !canSubmitClose) return;
    const name = masterSignName.trim();
    if (name.length < 2) {
      showError('Enter the master’s name / signature to close.');
      return;
    }
    setBusy(true);
    try {
      const closed = await closeMgaPeriod(item.id, { masterApprovedByName: name });
      setCloseConfirmOpen(false);
      setMasterSignName('');
      if (closed?.submittedForApproval) {
        showSuccess(
          closed?.resubmitted
            ? 'Close approval resubmitted. The period will close once approved.'
            : 'Period submitted for closing approval. It will close once approved.',
        );
      } else {
        const posting = closed?.posting as
          | { status?: string; documentId?: number; documentCode?: string | null; reason?: string }
          | undefined;
        if (posting?.status === 'posted') {
          showSuccess(
            `Period closed. Draft journal ${posting.documentCode || `#${posting.documentId}`} created.`,
          );
        } else if (posting?.status === 'skipped') {
          showSuccess(`Period closed. ${posting.reason || 'No finance journal created.'}`);
        } else {
          showSuccess('Period closed. Edits are locked until reopened.');
        }
      }
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, needsCloseResubmit ? 'Resubmit failed.' : 'Close failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!item || !canClose) return;
    setBusy(true);
    try {
      await reopenMgaPeriod(item.id);
      showSuccess('Period reopened.');
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Reopen failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (!Number.isInteger(periodId)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className={cn(colors.primaryFg, 'underline')}>
          Back to MGA
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>MGA period not found</h1>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('mt-4 inline-block text-sm underline', colors.primaryFg)}
        >
          Back to MGA
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', colors.background)}>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('shrink-0 border-b', colors.border)}>
          <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 space-y-1">
              <Link
                to={moduleConfig.module.route.detail(periodId)}
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium',
                  colors.textMuted,
                  'hover:underline',
                )}
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
                Dashboard
              </Link>
              <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                <h1 className={cn('truncate text-xl font-semibold tracking-tight', colors.text)}>
                  {item.vessel?.vesselName ?? `Vessel #${item.vesselId}`}
                </h1>
                <Badge variant={statusBadgeVariant(item.status)}>{formatMgaStatus(item.status)}</Badge>
                {dirty || notesDirty ? <Badge variant="secondary">Unsaved</Badge> : null}
              </div>
              <p className={cn('text-sm tabular-nums', colors.textMuted)}>
                {[
                  `${formatDateGlobal(item.periodFrom)} → ${formatDateGlobal(item.periodTo)}`,
                  item.currency || null,
                  `${lines.length} crew`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3 sm:justify-end">
              <div className="mr-1 min-w-[7.5rem] text-left sm:text-right">
                <p className={cn('text-[11px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Net due
                </p>
                <p className={cn('text-xl font-semibold tabular-nums leading-tight', colors.text)}>
                  {formatAmt(totals?.netDue)}
                </p>
              </div>
              {(canEdit ||
                canClose ||
                (editable && (tab === 'payroll' || tab === 'requests' || tab === 'notes'))) && (
                <div
                  className={cn(
                    'inline-flex flex-wrap items-center gap-1 rounded-xl border p-1 shadow-sm',
                    colors.border,
                    colors.surface,
                  )}
                >
                  {canEdit && item.status !== 'CLOSED' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled={busy}
                      onClick={handleImportFromPrior}
                      title="Import prior month — previous balances, remaining bonded stock, and opening cash"
                      aria-label="Import prior month"
                    >
                      <DownloadIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canEdit && item.status !== 'CLOSED' && tab === 'payroll' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled={busy}
                      onClick={handleGenerate}
                      title={lines.length ? 'Add missing crew' : 'Generate from crew list'}
                      aria-label={lines.length ? 'Add missing crew' : 'Generate from crew list'}
                    >
                      <UserPlusIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {editable && (tab === 'payroll' || tab === 'requests' || tab === 'notes') && (
                    <Button
                      type="button"
                      size="icon"
                      className="h-9 w-9"
                      disabled={busy || (tab === 'notes' ? !notesDirty : !dirty)}
                      onClick={handleSave}
                      title={
                        busy
                          ? 'Saving…'
                          : tab === 'notes'
                            ? 'Save notes'
                            : tab === 'requests'
                              ? 'Save requests'
                              : 'Save'
                      }
                      aria-label={
                        tab === 'notes' ? 'Save notes' : tab === 'requests' ? 'Save requests' : 'Save'
                      }
                    >
                      <SaveIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canSubmitClose && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled={busy}
                      onClick={handleClose}
                      title={needsCloseResubmit ? 'Resubmit close for approval' : 'Close period'}
                      aria-label={needsCloseResubmit ? 'Resubmit close for approval' : 'Close period'}
                    >
                      <LockIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canClose && closeApprovalPending && item.status === 'OPEN' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled
                      title="Close approval already pending"
                      aria-label="Close approval already pending"
                    >
                      <LockIcon className="h-4 w-4 opacity-50" />
                    </Button>
                  )}
                  {canClose && item.status === 'CLOSED' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      disabled={busy}
                      onClick={handleReopen}
                      title="Reopen period"
                      aria-label="Reopen period"
                    >
                      <UnlockIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-5 py-2.5',
              colors.border,
              colors.headerBg,
            )}
          >
            <TabsList variant="pill" fit>
              <TabsTrigger value="payroll" appearance="pill" size="sm" colorScheme="indigo" count={lines.length}>
                Payroll
              </TabsTrigger>
              <TabsTrigger value="cash" appearance="pill" size="sm" colorScheme="indigo">
                Cash / CTM
              </TabsTrigger>
              <TabsTrigger value="bonded" appearance="pill" size="sm" colorScheme="indigo">
                Bonded
              </TabsTrigger>
              <TabsTrigger value="requests" appearance="pill" size="sm" colorScheme="indigo">
                Crew requests
              </TabsTrigger>
              <TabsTrigger value="notes" appearance="pill" size="sm" colorScheme="indigo">
                Notes
              </TabsTrigger>
            </TabsList>
            {tab === 'payroll' &&
              wageColumns.some((c) => !['ALLOT', 'OTHER_DED'].includes(c.code.toUpperCase())) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowWageScalePersist(!showWageScale)}
                  title={showWageScale ? 'Collapse wage scale columns' : 'Expand wage scale columns'}
                >
                  <ViewColumnsIcon className="h-4 w-4" />
                  {showWageScale ? 'Collapse wages' : 'Expand wages'}
                </Button>
              )}
          </div>

          <TabsContent value="payroll" className="mt-0 flex min-h-0 flex-1 flex-col p-3">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {!lines.length ? (
                <EmptyPanel
                  message="No crew lines yet. Generate from the vessel crew list for this period."
                  action={
                    canEdit && item.status !== 'CLOSED' ? (
                      <Button type="button" size="sm" disabled={busy} onClick={handleGenerate}>
                        Generate from crew list
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <DataTable<MgaCrewLine>
                  data={lines}
                  columns={tableColumns}
                  keyExtractor={(row) => row.id}
                  emptyMessage="No crew lines."
                  showFooter={Boolean(totals)}
                  footerData={lines}
                  stickyHeader
                  density="compact"
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="cash" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
            <CashTab
              periodId={periodId}
              editable={editable}
              canEdit={canEdit && !locked}
              lines={lines}
              onChanged={() => mutate()}
            />
          </TabsContent>

          <TabsContent value="bonded" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
            <BondedTab
              periodId={periodId}
              editable={editable}
              canEdit={canEdit && !locked}
              lines={lines}
            />
          </TabsContent>

          <TabsContent value="requests" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
            <RequestsTab
              periodId={periodId}
              editable={editable}
              canEdit={canEdit && !locked}
              lines={lines}
              onChanged={() => mutate()}
            />
          </TabsContent>

          <TabsContent value="notes" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
            <div className={cn('mx-auto max-w-2xl rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
              <h2 className={cn('text-sm font-semibold', colors.text)}>MGA notes</h2>
              <p className={cn('mt-1 text-xs', colors.textMuted)}>
                Period remarks for the master / office (max 250 characters).
              </p>
              <div className="mt-4">
                <Textarea
                  value={notesDraft}
                  onChange={(e) => {
                    setNotesDraft(e.target.value);
                    setNotesDirty(true);
                  }}
                  rows={10}
                  maxLength={250}
                  showCharacterCount
                  disabled={!editable}
                  readOnly={!editable}
                  placeholder={editable ? 'Add notes for this MGA period…' : 'No notes'}
                />
              </div>
              {editable && (
                <div className="mt-4 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    disabled={busy || !notesDirty}
                    onClick={handleSave}
                  >
                    {busy ? 'Saving…' : 'Save notes'}
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Modal isOpen={Boolean(cellEdit)} onClose={closeCellEdit} size="sm">
        <ModalHeader>
          <ModalTitle>Edit {cellEdit?.columnCode ?? 'value'}</ModalTitle>
          <Button type="button" variant="ghost" size="sm" onClick={closeCellEdit}>
            Close
          </Button>
        </ModalHeader>
        <ModalContent>
          <p className={cn('mb-3 text-sm', colors.textSecondary)}>
            {cellEdit?.crewName}
            {cellEdit?.columnName ? ` · ${cellEdit.columnName}` : ''}
          </p>
          <label className={cn('mb-1.5 block text-sm font-medium', colors.text)}>
            {cellEdit?.valueLabel ?? 'Value'}
          </label>
          {cellEdit?.inputKind === 'text' ? (
            <TextInput
              value={cellEditValue}
              onChange={(e) => setCellEditValue(e.target.value)}
              maxLength={250}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCellEdit();
                }
              }}
            />
          ) : (
            <NumberInput
              value={cellEditValue}
              onChange={(e) => setCellEditValue(e.target.value == null ? '' : String(e.target.value))}
              allowDecimals
              showClearButton={false}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCellEdit();
                }
              }}
            />
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={closeCellEdit}>
            Cancel
          </Button>
          <Button type="button" onClick={applyCellEdit}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={closeConfirmOpen}
        onClose={() => {
          setCloseConfirmOpen(false);
          setMasterSignName('');
        }}
        size="sm"
      >
        <ModalHeader>
          <ModalTitle>{needsCloseResubmit ? 'Resubmit close for approval?' : 'Close MGA period?'}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <p className={cn('mb-3 text-sm', colors.textSecondary)}>
            {needsCloseResubmit
              ? 'Office returned this close for changes. After you fix the sheet, enter the master’s name and resubmit for approval.'
              : 'Closing locks payroll, cash, bonded, and crew requests. Enter the master’s name as approval / signature.'}
          </p>
          {needsCloseResubmit && item?.closeApproval?.lastReturn?.comments ? (
            <p className={cn('mb-3 rounded-lg border px-3 py-2 text-sm italic', colors.border, colors.text)}>
              "{item.closeApproval.lastReturn.comments}"
            </p>
          ) : null}
          <label className="block space-y-1">
            <span className={cn('text-sm font-medium', colors.text)}>Master name / signature</span>
            <TextInput
              value={masterSignName}
              onChange={(e) => setMasterSignName(e.target.value)}
              placeholder="e.g. Capt. J. Smith"
              maxLength={150}
              autoFocus
            />
          </label>
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setCloseConfirmOpen(false);
              setMasterSignName('');
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            disabled={busy || masterSignName.trim().length < 2}
            onClick={confirmClose}
          >
            {busy
              ? needsCloseResubmit
                ? 'Resubmitting…'
                : 'Closing…'
              : needsCloseResubmit
                ? 'Resubmit for approval'
                : 'Close period'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

function EmptyPanel({
  message,
  action,
}: {
  message: string;
  action?: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <p className={cn('max-w-sm text-sm', colors.textMuted)}>{message}</p>
      {action}
    </div>
  );
}

function PanelShell({
  title,
  total,
  children,
  empty,
  emptyMessage,
}: {
  title: string;
  total?: React.ReactNode;
  children: React.ReactNode;
  empty?: boolean;
  emptyMessage?: string;
}) {
  const { colors } = useTheme();
  return (
    <div className={cn('flex min-h-0 flex-col overflow-hidden rounded-lg border', colors.border, colors.surface)}>
      <div className={cn('flex items-center justify-between gap-2 border-b px-3 py-2', colors.border)}>
        <p className={cn('text-sm font-semibold', colors.text)}>{title}</p>
        {total != null && (
          <span className={cn('text-xs font-semibold tabular-nums', colors.textSecondary)}>{total}</span>
        )}
      </div>
      {empty ? <EmptyPanel message={emptyMessage || 'Nothing here yet.'} /> : children}
    </div>
  );
}

function CashTab({
  periodId,
  editable,
  canEdit,
  lines,
  onChanged,
}: {
  periodId: number;
  editable: boolean;
  canEdit: boolean;
  lines: MgaCrewLine[];
  onChanged?: () => void;
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { data, mutate, isLoading } = useMgaCashEntries(periodId);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    entryType: 'INCOME' as MgaCashEntryType,
    entryDate: todayIso(),
    description: '',
    amount: '',
    currency: 'USD',
    exchangeRate: '1',
    remarks: '',
    mgaCrewLineId: '',
  });

  const resetForm = (keepType = true) =>
    setForm({
      entryType: keepType ? form.entryType : 'INCOME',
      entryDate: todayIso(),
      description: '',
      amount: '',
      currency: form.currency || 'USD',
      exchangeRate: '1',
      remarks: '',
      mgaCrewLineId: '',
    });

  const closeForm = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const openForm = (entryType: MgaCashEntryType = 'INCOME') => {
    setEditingId(null);
    setForm({
      entryType,
      entryDate: todayIso(),
      description: '',
      amount: '',
      currency: 'USD',
      exchangeRate: '1',
      remarks: '',
      mgaCrewLineId: '',
    });
    setFormOpen(true);
  };

  const openEdit = (entry: MgaCashEntry) => {
    if (!canEdit || !editable) return;
    setEditingId(entry.id);
    const dateRaw = entry.entryDate ? String(entry.entryDate).slice(0, 10) : todayIso();
    setForm({
      entryType: entry.entryType,
      entryDate: dateRaw,
      description: entry.description || '',
      amount: entry.amount != null ? String(entry.amount) : '',
      currency: entry.currency || 'USD',
      exchangeRate: entry.exchangeRate != null ? String(entry.exchangeRate) : '1',
      remarks: entry.remarks || '',
      mgaCrewLineId: entry.mgaCrewLineId != null ? String(entry.mgaCrewLineId) : '',
    });
    setFormOpen(true);
  };

  const income = data.filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM');
  const expense = data.filter((e) => e.entryType === 'EXPENSE');
  const incomeTotal = income.reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
  const expenseTotal = expense.reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
  const bal = data.reduce((s, e) => {
    const amt = Number(e.amountPeriod ?? e.amount) || 0;
    if (e.entryType === 'EXPENSE') return s - amt;
    return s + amt;
  }, 0);

  const save = async () => {
    if (!canEdit || !form.description.trim()) return;
    const payload = {
      entryType: form.entryType,
      entryDate: form.entryDate || null,
      description: form.description,
      amount: Number(form.amount) || 0,
      currency: form.currency,
      exchangeRate: Number(form.exchangeRate) || 1,
      remarks: form.remarks || null,
      mgaCrewLineId:
        form.entryType === 'EXPENSE' && form.mgaCrewLineId
          ? Number(form.mgaCrewLineId)
          : null,
    };
    setBusy(true);
    try {
      if (editingId != null) {
        await updateMgaCashEntry(periodId, editingId, payload);
        showSuccess(
          form.entryType === 'EXPENSE' && form.mgaCrewLineId
            ? 'Cash entry updated (Master adv. refreshed).'
            : 'Cash entry updated.',
        );
      } else {
        await createMgaCashEntry(periodId, payload);
        showSuccess(
          form.entryType === 'EXPENSE' && form.mgaCrewLineId
            ? 'Cash entry added (Master adv. updated).'
            : 'Cash entry added.',
        );
      }
      setFormOpen(false);
      setEditingId(null);
      resetForm();
      await mutate();
      onChanged?.();
    } catch (err) {
      showError(errMsg(err, editingId != null ? 'Update failed.' : 'Failed to add entry.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry: MgaCashEntry) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      await deleteMgaCashEntry(periodId, entry.id);
      showSuccess('Entry removed.');
      await mutate();
      onChanged?.();
    } catch (err) {
      showError(errMsg(err, 'Delete failed.'));
    } finally {
      setBusy(false);
    }
  };

  const cashCols = (kind: 'in' | 'out'): Column<MgaCashEntry>[] => [
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (_v: unknown, row: MgaCashEntry) => (
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', colors.primaryFg)}
                  disabled={busy || !editable}
                  onClick={() => openEdit(row)}
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', dangerDeleteBtn(isDark))}
                  disabled={busy || !editable}
                  onClick={() => remove(row)}
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            ),
          } as Column<MgaCashEntry>,
        ]
      : []),
    {
      key: 'entryDate',
      header: 'Date',
      render: (v) => (
        <span className="text-sm whitespace-nowrap">
          {v ? formatDateGlobal(String(v)) : '—'}
        </span>
      ),
    },
    {
      key: 'entryType',
      header: 'Type',
      render: (v) => <span className="text-sm">{String(v)}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (v) => <span className="text-sm">{String(v)}</span>,
    },
    {
      key: 'currency',
      header: 'CCY',
      render: (v) => <span className="text-sm">{String(v)}</span>,
    },
    {
      key: 'exchangeRate',
      header: 'FX',
      align: 'right',
      render: (_v, row) => (
        <span className="text-sm tabular-nums">{formatAmt(row.exchangeRate ?? 1)}</span>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (v, row) => (
        <span className={cn('text-sm tabular-nums', kind === 'out' && dangerAmt(isDark))}>
          {formatAmt(v)}
          {row.currency && row.exchangeRate != null && Number(row.exchangeRate) !== 1
            ? ` ${row.currency}`
            : ''}
        </span>
      ),
    },
    {
      key: 'amountPeriod',
      header: 'Period amt',
      align: 'right',
      footerType: 'custom',
      footerRender: (_v, all) =>
        formatAmt(all.reduce((s, r) => s + (Number(r.amountPeriod ?? r.amount) || 0), 0)),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', kind === 'out' && dangerAmt(isDark))}>
          {formatAmt(row.amountPeriod ?? row.amount)}
        </span>
      ),
    },
    {
      key: 'crewLine',
      header: 'Crew',
      render: (_v, row) => (
        <span className="text-sm">{row.crewLine?.crewName || '—'}</span>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs shadow-sm',
            colors.border,
            colors.surface,
          )}
        >
          <span className={colors.textSecondary}>Balance</span>
          <span className={cn('font-semibold tabular-nums', colors.text)}>{formatAmt(bal)}</span>
        </div>
        {canEdit && (
          <Button type="button" size="sm" disabled={!editable} onClick={() => openForm('INCOME')}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" />
            Add entry
          </Button>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <PanelShell
          title="Income / CTM"
          total={formatAmt(incomeTotal)}
          empty={!isLoading && income.length === 0}
          emptyMessage="No income entries."
        >
          <DataTable
            data={income}
            columns={cashCols('in')}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No income entries."
            showFooter
            footerData={income}
            density="compact"
          />
        </PanelShell>
        <PanelShell
          title="Expenses (crew advances)"
          total={<span className={dangerAmt(isDark)}>{formatAmt(expenseTotal)}</span>}
          empty={!isLoading && expense.length === 0}
          emptyMessage="No expense entries."
        >
          <DataTable
            data={expense}
            columns={cashCols('out')}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No expense entries."
            showFooter
            footerData={expense}
            density="compact"
          />
        </PanelShell>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} size="sm">
        <ModalHeader>
          <ModalTitle>{editingId != null ? 'Edit cash entry' : 'Add cash entry'}</ModalTitle>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={closeForm}>
            Close
          </Button>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Type</span>
              <select
                className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                value={form.entryType}
                onChange={(e) => {
                  const entryType = e.target.value as MgaCashEntryType;
                  setForm((f) => ({
                    ...f,
                    entryType,
                    mgaCrewLineId: entryType === 'EXPENSE' ? f.mgaCrewLineId : '',
                  }));
                }}
              >
                <option value="INCOME">Income</option>
                <option value="CTM">CTM</option>
                <option value="EXPENSE">Expense</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Date</span>
              <DateInput
                value={form.entryDate}
                onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value || todayIso() }))}
              />
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Description</span>
              <TextInput
                placeholder="e.g. Previous balance / remittance"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                autoFocus
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className={cn('text-sm font-medium', colors.text)}>Amount</span>
                <NumberInput
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: e.target.value == null ? '' : String(e.target.value) }))
                  }
                  allowDecimals
                  showClearButton={false}
                />
              </label>
              <label className="block space-y-1">
                <span className={cn('text-sm font-medium', colors.text)}>Currency</span>
                <TextInput
                  placeholder="USD"
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  maxLength={3}
                />
              </label>
            </div>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>
                FX rate (→ period currency)
              </span>
              <NumberInput
                placeholder="1"
                value={form.exchangeRate}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    exchangeRate: e.target.value == null ? '1' : String(e.target.value),
                  }))
                }
                allowDecimals
                showClearButton={false}
              />
              <p className={cn('text-xs', colors.textMuted)}>
                Period amount ≈ {(Number(form.amount) || 0) * (Number(form.exchangeRate) || 1)}
              </p>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Crew (optional)</span>
              <select
                className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                value={form.mgaCrewLineId}
                onChange={(e) => setForm((f) => ({ ...f, mgaCrewLineId: e.target.value }))}
                disabled={form.entryType !== 'EXPENSE'}
              >
                <option value="">None</option>
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.crewName}
                  </option>
                ))}
              </select>
              <p className={cn('text-xs', colors.textMuted)}>
                {form.entryType === 'EXPENSE'
                  ? 'Linking a crew member updates Master adv. on the payroll sheet.'
                  : 'Crew link applies to expense (cash advance) entries only.'}
              </p>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Remarks</span>
              <TextInput
                placeholder="Optional notes"
                value={form.remarks}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                maxLength={250}
              />
            </label>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" disabled={busy} onClick={closeForm}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !editable || !form.description.trim()}
            onClick={save}
          >
            {busy ? 'Saving…' : editingId != null ? 'Save changes' : 'Add entry'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function BondedTab({
  periodId,
  editable,
  canEdit,
  lines,
}: {
  periodId: number;
  editable: boolean;
  canEdit: boolean;
  lines: MgaCrewLine[];
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { data, mutate, isLoading } = useMgaBondedLines(periodId);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    section: 'STOCK' as MgaBondedSection,
    entryDate: todayIso(),
    description: '',
    unitPrice: '',
    qty: '',
    mgaCrewLineId: '',
  });

  const closeForm = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const openForm = (section: MgaBondedSection = 'STOCK') => {
    setEditingId(null);
    setForm({
      section,
      entryDate: todayIso(),
      description: '',
      unitPrice: '',
      qty: '',
      mgaCrewLineId: '',
    });
    setFormOpen(true);
  };

  const openEdit = (line: MgaBondedLine) => {
    if (!canEdit || !editable) return;
    setEditingId(line.id);
    const dateRaw = line.entryDate ? String(line.entryDate).slice(0, 10) : todayIso();
    const section: MgaBondedSection =
      line.section === 'INCOMING' ? 'STOCK' : line.section;
    setForm({
      section,
      entryDate: dateRaw,
      description: line.description || '',
      unitPrice: line.unitPrice != null ? String(line.unitPrice) : '',
      qty: line.qty != null ? String(line.qty) : '',
      mgaCrewLineId: line.mgaCrewLineId != null ? String(line.mgaCrewLineId) : '',
    });
    setFormOpen(true);
  };

  const stock = data.filter((l) => l.section === 'STOCK' || l.section === 'INCOMING');
  const expense = data.filter((l) => l.section === 'EXPENSE');
  const stockTotal = stock.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const expenseTotal = expense.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const balance = stockTotal - expenseTotal;

  const save = async () => {
    if (!canEdit || !form.description.trim()) return;
    const unitPrice = Number(form.unitPrice) || 0;
    const qty = Number(form.qty) || 0;
    const payload = {
      section: form.section,
      entryDate: form.entryDate || null,
      description: form.description,
      unitPrice,
      qty,
      amount: unitPrice * qty,
      mgaCrewLineId:
        form.section === 'EXPENSE' && form.mgaCrewLineId
          ? Number(form.mgaCrewLineId)
          : null,
    };
    setBusy(true);
    try {
      if (editingId != null) {
        await updateMgaBondedLine(periodId, editingId, payload);
        showSuccess('Bonded line updated.');
      } else {
        await createMgaBondedLine(periodId, payload);
        showSuccess('Bonded line added.');
      }
      setFormOpen(false);
      setEditingId(null);
      setForm({
        section: form.section,
        entryDate: todayIso(),
        description: '',
        unitPrice: '',
        qty: '',
        mgaCrewLineId: '',
      });
      await mutate();
    } catch (err) {
      showError(errMsg(err, editingId != null ? 'Update failed.' : 'Failed to add line.'));
    } finally {
      setBusy(false);
    }
  };

  const copyPrior = async () => {
    if (!canEdit) return;
    setBusy(true);
    try {
      const res = await generateMgaBondedFromPrior(periodId);
      showSuccess(
        res.generatedCount
          ? `Copied ${res.generatedCount} remaining stock line(s) from prior CLOSED MGA.`
          : 'No remaining stock to copy from prior CLOSED MGA.',
      );
      await mutate();
    } catch (err) {
      showError(errMsg(err, 'Copy failed.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (line: MgaBondedLine) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      await deleteMgaBondedLine(periodId, line.id);
      showSuccess('Bonded line removed.');
      await mutate();
    } catch (err) {
      showError(errMsg(err, 'Delete failed.'));
    } finally {
      setBusy(false);
    }
  };

  const cols = (section: MgaBondedSection): Column<MgaBondedLine>[] => [
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (_v: unknown, row: MgaBondedLine) => (
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', colors.primaryFg)}
                  disabled={busy || !editable}
                  onClick={() => openEdit(row)}
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', dangerDeleteBtn(isDark))}
                  disabled={busy || !editable}
                  onClick={() => remove(row)}
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            ),
          } as Column<MgaBondedLine>,
        ]
      : []),
    {
      key: 'entryDate',
      header: 'Date',
      render: (v) => (
        <span className="text-sm whitespace-nowrap">
          {v ? formatDateGlobal(String(v)) : '—'}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (v) => <span className="text-sm">{String(v)}</span>,
    },
    {
      key: 'unitPrice',
      header: 'Unit',
      align: 'right',
      render: (v) => <span className="text-sm tabular-nums">{formatAmt(v)}</span>,
    },
    {
      key: 'qty',
      header: 'Qty',
      align: 'right',
      render: (v) => <span className="text-sm tabular-nums">{formatAmt(v)}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      footerType: 'custom',
      footerRender: (_v, all) => formatAmt(all.reduce((s, r) => s + (Number(r.amount) || 0), 0)),
      render: (v) => (
        <span className={cn('text-sm tabular-nums', section === 'EXPENSE' && dangerAmt(isDark))}>
          {formatAmt(v)}
        </span>
      ),
    },
    ...(section === 'EXPENSE'
      ? [
          {
            key: 'crewLine',
            header: 'Crew',
            render: (_v: unknown, row: MgaBondedLine) => (
              <span className="text-sm">{row.crewLine?.crewName || '—'}</span>
            ),
          } as Column<MgaBondedLine>,
        ]
      : []),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs shadow-sm',
            colors.border,
            colors.surface,
          )}
        >
          <span className={colors.textSecondary}>Balance</span>
          <span className={cn('font-semibold tabular-nums', colors.text)}>{formatAmt(balance)}</span>
          <span className={colors.textMuted}>(incoming − expense)</span>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={busy || !editable} onClick={copyPrior}>
              Copy prior remaining stock
            </Button>
            <Button type="button" size="sm" disabled={!editable} onClick={() => openForm('STOCK')}>
              <PlusIcon className="mr-1 h-3.5 w-3.5" />
              Add line
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <PanelShell
          title="Incoming"
          total={formatAmt(stockTotal)}
          empty={!isLoading && stock.length === 0}
          emptyMessage="No incoming lines."
        >
          <DataTable
            data={stock}
            columns={cols('STOCK')}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No incoming lines."
            showFooter
            footerData={stock}
            density="compact"
          />
        </PanelShell>
        <PanelShell
          title="Expenses"
          total={<span className={dangerAmt(isDark)}>{formatAmt(expenseTotal)}</span>}
          empty={!isLoading && expense.length === 0}
          emptyMessage="No expense lines."
        >
          <DataTable
            data={expense}
            columns={cols('EXPENSE')}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No expense lines."
            showFooter
            footerData={expense}
            density="compact"
          />
        </PanelShell>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} size="sm">
        <ModalHeader>
          <ModalTitle>{editingId != null ? 'Edit bonded line' : 'Add bonded line'}</ModalTitle>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={closeForm}>
            Close
          </Button>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Section</span>
              <select
                className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                value={form.section}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    section: e.target.value as MgaBondedSection,
                    mgaCrewLineId: e.target.value === 'EXPENSE' ? f.mgaCrewLineId : '',
                  }))
                }
              >
                <option value="STOCK">Incoming</option>
                <option value="EXPENSE">Expense</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Date</span>
              <DateInput
                value={form.entryDate}
                onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value || todayIso() }))}
              />
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Description</span>
              <TextInput
                placeholder="Item description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                autoFocus
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className={cn('text-sm font-medium', colors.text)}>Unit price</span>
                <NumberInput
                  placeholder="0.00"
                  value={form.unitPrice}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      unitPrice: e.target.value == null ? '' : String(e.target.value),
                    }))
                  }
                  allowDecimals
                  showClearButton={false}
                />
              </label>
              <label className="block space-y-1">
                <span className={cn('text-sm font-medium', colors.text)}>Qty</span>
                <NumberInput
                  placeholder="0"
                  value={form.qty}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, qty: e.target.value == null ? '' : String(e.target.value) }))
                  }
                  allowDecimals
                  showClearButton={false}
                />
              </label>
            </div>
            <p className={cn('text-xs tabular-nums', colors.textMuted)}>
              Amount: {formatAmt((Number(form.unitPrice) || 0) * (Number(form.qty) || 0))}
            </p>
            {form.section === 'EXPENSE' && (
              <label className="block space-y-1">
                <span className={cn('text-sm font-medium', colors.text)}>Crew (optional)</span>
                <select
                  className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                  value={form.mgaCrewLineId}
                  onChange={(e) => setForm((f) => ({ ...f, mgaCrewLineId: e.target.value }))}
                >
                  <option value="">None</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.crewName}
                      {l.rankName ? ` — ${l.rankName}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" disabled={busy} onClick={closeForm}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !editable || !form.description.trim()}
            onClick={save}
          >
            {busy ? 'Saving…' : editingId != null ? 'Save changes' : 'Add line'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function RequestsTab({
  periodId,
  editable,
  canEdit,
  lines,
  onChanged,
}: {
  periodId: number;
  editable: boolean;
  canEdit: boolean;
  lines: MgaCrewLine[];
  onChanged?: () => void | Promise<unknown>;
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { data, mutate, isLoading } = useMgaCrewRequests(periodId);
  const [busy, setBusy] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    requestType: 'CASH_BY_MASTER' as MgaCrewRequestType,
    entryDate: todayIso(),
    mgaCrewLineId: '',
    amount: '',
    remarks: '',
  });

  const closeForm = () => {
    if (busy) return;
    setFormOpen(false);
    setEditingId(null);
  };

  const openForm = (requestType: MgaCrewRequestType = 'CASH_BY_MASTER') => {
    setEditingId(null);
    setForm({
      requestType,
      entryDate: todayIso(),
      mgaCrewLineId: lines[0] ? String(lines[0].id) : '',
      amount: '',
      remarks: '',
    });
    setFormOpen(true);
  };

  const openEdit = (row: MgaCrewRequest) => {
    if (!canEdit || !editable) return;
    setEditingId(row.id);
    const dateRaw = row.entryDate ? String(row.entryDate).slice(0, 10) : todayIso();
    setForm({
      requestType: row.requestType,
      entryDate: dateRaw,
      mgaCrewLineId: row.mgaCrewLineId != null ? String(row.mgaCrewLineId) : '',
      amount: row.amount != null ? String(row.amount) : '',
      remarks: row.remarks || '',
    });
    setFormOpen(true);
  };

  const cashRows = data.filter((r) => r.requestType === 'CASH_BY_MASTER');
  const allotRows = data.filter((r) => r.requestType === 'ALLOTMENT');
  const cashTotal = cashRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const allotTotal = allotRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const save = async () => {
    if (!canEdit || !form.mgaCrewLineId) return;
    const payload = {
      requestType: form.requestType,
      entryDate: form.entryDate || null,
      mgaCrewLineId: Number(form.mgaCrewLineId),
      amount: Number(form.amount) || 0,
      remarks: form.remarks || null,
    };
    setBusy(true);
    try {
      if (editingId != null) {
        await updateMgaCrewRequest(periodId, editingId, payload);
        showSuccess(
          form.requestType === 'ALLOTMENT'
            ? 'Request updated (ALLOT refreshed).'
            : 'Request updated (Master adv. refreshed).',
        );
      } else {
        await createMgaCrewRequest(periodId, payload);
        showSuccess(
          form.requestType === 'ALLOTMENT'
            ? 'Request added (ALLOT updated).'
            : 'Request added (Master adv. updated).',
        );
      }
      setFormOpen(false);
      setEditingId(null);
      setForm({
        requestType: form.requestType,
        entryDate: todayIso(),
        mgaCrewLineId: form.mgaCrewLineId,
        amount: '',
        remarks: '',
      });
      await mutate();
      await onChanged?.();
    } catch (err) {
      showError(errMsg(err, editingId != null ? 'Update failed.' : 'Failed to add request.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: MgaCrewRequest) => {
    if (!canEdit) return;
    setBusy(true);
    try {
      await deleteMgaCrewRequest(periodId, row.id);
      showSuccess('Request removed.');
      await mutate();
      await onChanged?.();
    } catch (err) {
      showError(errMsg(err, 'Delete failed.'));
    } finally {
      setBusy(false);
    }
  };

  const cols = (): Column<MgaCrewRequest>[] => [
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: '',
            render: (_v: unknown, row: MgaCrewRequest) => (
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', colors.primaryFg)}
                  disabled={busy || !editable}
                  onClick={() => openEdit(row)}
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn('h-8 w-8', dangerDeleteBtn(isDark))}
                  disabled={busy || !editable}
                  onClick={() => remove(row)}
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            ),
          } as Column<MgaCrewRequest>,
        ]
      : []),
    {
      key: 'entryDate',
      header: 'Date',
      render: (v) => (
        <span className="text-sm whitespace-nowrap">{v ? formatDateGlobal(String(v)) : '—'}</span>
      ),
    },
    {
      key: 'crewLine',
      header: 'Crew',
      render: (_v, row) => <span className="text-sm">{row.crewLine?.crewName || '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      footerType: 'custom',
      footerRender: (_v, all) => formatAmt(all.reduce((s, r) => s + (Number(r.amount) || 0), 0)),
      render: (v) => <span className="text-sm tabular-nums">{formatAmt(v)}</span>,
    },
    {
      key: 'remarks',
      header: 'Remarks',
      render: (v) => <span className="text-sm">{(v as string) || '—'}</span>,
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={cn('text-xs', colors.textMuted)}>
          Add cash-by-master requests (feeds Master adv.) or allotments (feeds ALLOT), each with its own date.
        </p>
        {canEdit && (
          <Button type="button" size="sm" disabled={!editable || lines.length === 0} onClick={() => openForm()}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" />
            Add request
          </Button>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <PanelShell
          title="Cash by master"
          total={formatAmt(cashTotal)}
          empty={!isLoading && cashRows.length === 0}
          emptyMessage="No cash-by-master requests."
        >
          <DataTable
            data={cashRows}
            columns={cols()}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No cash-by-master requests."
            showFooter
            footerData={cashRows}
            density="compact"
          />
        </PanelShell>
        <PanelShell
          title="Allotments"
          total={formatAmt(allotTotal)}
          empty={!isLoading && allotRows.length === 0}
          emptyMessage="No allotment requests."
        >
          <DataTable
            data={allotRows}
            columns={cols()}
            keyExtractor={(r) => r.id}
            loading={isLoading}
            emptyMessage="No allotment requests."
            showFooter
            footerData={allotRows}
            density="compact"
          />
        </PanelShell>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} size="sm">
        <ModalHeader>
          <ModalTitle>{editingId != null ? 'Edit crew request' : 'Add crew request'}</ModalTitle>
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={closeForm}>
            Close
          </Button>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Type</span>
              <select
                className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                value={form.requestType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, requestType: e.target.value as MgaCrewRequestType }))
                }
              >
                <option value="CASH_BY_MASTER">Cash by master</option>
                <option value="ALLOTMENT">Allotment</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Date</span>
              <DateInput
                value={form.entryDate}
                onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value || todayIso() }))}
              />
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Crew</span>
              <select
                className={cn('h-10 w-full rounded-md border px-2 text-sm', colors.border, colors.surface)}
                value={form.mgaCrewLineId}
                onChange={(e) => setForm((f) => ({ ...f, mgaCrewLineId: e.target.value }))}
              >
                <option value="">Select crew</option>
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.crewName}
                    {l.rankName ? ` — ${l.rankName}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Amount</span>
              <NumberInput
                placeholder="0.00"
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value == null ? '' : String(e.target.value) }))
                }
                allowDecimals
                showClearButton={false}
                autoFocus
              />
            </label>
            <label className="block space-y-1">
              <span className={cn('text-sm font-medium', colors.text)}>Remarks</span>
              <TextInput
                placeholder="Optional notes"
                value={form.remarks}
                onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
                maxLength={250}
              />
            </label>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" disabled={busy} onClick={closeForm}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={busy || !editable || !form.mgaCrewLineId}
            onClick={save}
          >
            {busy ? 'Saving…' : editingId != null ? 'Save changes' : 'Add request'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default PeriodGrid;
